<template>
  <div class="container compare-selector">
    <p class="px-0 pt-3 pb-1 text-muted" style="font-size: 0.85rem;">
      {{ $t('base_map_dialog.compare_maps_selector_view.choose_a_map_to_overlay_onto') }}
    </p>

    <!-- Standard Maps -->
    <div class="col-12 px-0 pt-2 pb-2">
      <p class="px-0 mb-0"><small><strong>{{ $t('base_map_dialog.compare_maps_selector_view.standard_maps') }}</strong></small></p>
      <div class="mapstyle-list">
        <div
          v-for="(style, index) in freeStyles"
          :key="index"
          class="position-relative d-inline-block"
          @click="$emit('select', style)"
        >
          <div class="mapstyle-card" :class="{ 'current-style': isActive(style) }">
            <img loading="lazy" :src="style.image" :alt="$t('shared.map')" draggable="false" />
            <span v-if="isActive(style)" class="active-badge">{{ $t('base_map_dialog.compare_maps_selector_view.active') }}</span>
            <div class="hiking-text p-1">
              <small><p class="m-0"><strong>{{ style.displayName() }}</strong></p></small>
              <small><p class="text-muted">{{ style.isPro ? 'PRO+' : $t('base_map_dialog.compare_maps_selector_view.free') }}</p></small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PRO+ Map Collections -->
    <div class="col-12 px-0 py-1">
      <p class="px-0 mb-0"><small><strong>{{ $t('base_map_dialog.compare_maps_selector_view.pro_map_collections') }}</strong></small></p>
      <div class="mapstyle-list">
        <div
          v-for="(style, index) in proStyles"
          :key="index"
          class="position-relative d-inline-block"
          @click="$emit('select', style)"
        >
          <div class="mapstyle-card" :class="{ 'current-style': isActive(style) }">
            <img loading="lazy" :src="style.image" :alt="$t('shared.map')" draggable="false" />
            <span v-if="isActive(style)" class="active-badge">{{ $t('base_map_dialog.compare_maps_selector_view.active_2') }}</span>
            <div class="hiking-text p-1">
              <small><p class="m-0"><strong>{{ style.displayName() }}</strong></p></small>
              <small><p class="text-muted">{{ $t('shared.pro') }}</p></small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { HiikerMapStyle } from '../../../utils/models/MapStyles';

export default defineComponent({
  name: 'CompareMapsSelectorView',
  props: {
    currentMapStyle: { type: Object as () => HiikerMapStyle | null, default: null },
  },
  emits: ['select'],
  computed: {
    allStyles(): HiikerMapStyle[] {
      return HiikerMapStyle.getAllStyles();
    },
    freeStyles(): HiikerMapStyle[] {
      return this.allStyles.filter(s => !s.isPro);
    },
    proStyles(): HiikerMapStyle[] {
      return this.allStyles.filter(s => s.isPro);
    },
  },
  methods: {
    isActive(style: HiikerMapStyle): boolean {
      return !!this.currentMapStyle && this.currentMapStyle.name === style.name;
    },
  },
});
</script>

<style scoped>
.compare-selector { padding-bottom: 1rem; }

/* ── identical to BaseMapStylesDialog card strip ── */
.mapstyle-list {
  display: flex;
  overflow-x: auto;
  padding-bottom: 1em;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  user-select: none;
}
.mapstyle-list::-webkit-scrollbar { display: none; }
.mapstyle-list > .position-relative { flex: 0 0 auto; margin-right: 0; }
.mapstyle-list > .position-relative:first-child { margin-left: 0; }

.mapstyle-card {
  position: relative;
  width: 120px;
  background-color: #fff;
  border: 2px solid #434343;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  margin-top: 1em;
  margin-right: 1em;
}
.mapstyle-card img { width: 100%; height: auto; }
.mapstyle-card .hiking-text {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  color: #fff;
  background-color: rgba(0, 0, 0, 0.7);
}
.current-style { border: 2px solid #fddf00; }

/* Active badge — sits top-left over the image */
.active-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 1;
  background: #fddf00;
  color: #17181d;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 20px;
}

@media (max-width: 475px) {
  .mapstyle-card { width: 108px; margin-right: 0.7em; }
}
</style>
